/**
 * ─── HRIS Schema — Types & Zod Schemas ───────────────────────
 * Human Resource Information System with Philippine compliance.
 *
 * Entities:
 *  - Employee       — Master employee record (personal, tax, government IDs)
 *  - Attendance     — Time logs, biometric entries, shifts
 *  - PayrollPeriod  — Pay period definition & processing runs
 *  - Payslip        — Generated payslip per employee per period
 *  - Form2316       — Annual BIR Certificate of Compensation Payment
 *
 * Philippine Compliance:
 *  - BIR Form 2316 (Certificate of Compensation Payment/Tax Withheld)
 *  - SSS, PhilHealth, Pag-IBIG contributions
 *  - TRAIN Law graduated withholding tax table
 *  - 13th Month Pay, De Minimis benefits, Overtime pay rates
 */

import { z } from 'zod'
import { notesSchema, createQuerySchema, createUpdateSchema, emailSchema, phoneSchema } from '@repo/core'

// ═══════════════════════════════════════════════════════════════
// Employee
// ═══════════════════════════════════════════════════════════════

export type CivilStatus = 'single' | 'married' | 'widowed' | 'separated' | 'divorced'
export type EmploymentStatus = 'probationary' | 'regular' | 'contractual' | 'project_based' | 'seasonal' | 'resigned' | 'terminated' | 'retired'
export type TaxStatus = 'S' | 'S1' | 'S2' | 'S3' | 'S4' | 'ME' | 'ME1' | 'ME2' | 'ME3' | 'ME4'
export type PayFrequency = 'daily' | 'weekly' | 'semi_monthly' | 'monthly'
export type Sex = 'male' | 'female'

export interface Employee {
  id: string
  tenantId: string
  branchId?: string

  // ─── Personal Info ──────────────────────────────────────
  employeeCode: string
  firstName: string
  lastName: string
  fullName: string
  middleName?: string
  suffix?: string               // Jr, Sr, III
  sex: Sex
  civilStatus: CivilStatus
  dateOfBirth: string            // YYYY-MM-DD
  birthplace?: string
  nationality: string
  phone: string
  email?: string
  address: string
  barangay?: string
  city: string
  province: string
  photoUrl?: string

  // ─── Employment ─────────────────────────────────────────
  department: string
  position: string
  employmentStatus: EmploymentStatus
  dateHired: string
  dateRegularized?: string
  dateResigned?: string
  payFrequency: PayFrequency
  basicSalary: number            // Monthly basic salary (PHP)

  // ─── Tax Information (for Form 2316) ────────────────────
  tin: string                    // Tax Identification Number
  taxStatus: TaxStatus           // S/ME + number of qualified dependents
  hasOtherEmployer: boolean      // If employee has multiple employers
  otherEmployerName?: string
  sssNumber: string
  philhealthNumber: string
  pagibigNumber: string
  pagibigContribution: number    // Default 100 or 200 depending on salary

  // ─── Bank Details ───────────────────────────────────────
  bankName?: string
  bankAccountNumber?: string
  bankAccountType?: 'savings' | 'checking'

  // ─── Emergency Contact ──────────────────────────────────
  emergencyContactName: string
  emergencyContactPhone: string
  emergencyContactRelation?: string

  // ─── Metadata-driven fields (per tenant customization) ──
  customFields?: Record<string, unknown>

  // ─── Base entity ────────────────────────────────────────
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CivilStatusSchema = z.enum(['single', 'married', 'widowed', 'separated', 'divorced'])
export const EmploymentStatusSchema = z.enum([
  'probationary', 'regular', 'contractual', 'project_based',
  'seasonal', 'resigned', 'terminated', 'retired',
])
export const TaxStatusSchema = z.enum([
  'S', 'S1', 'S2', 'S3', 'S4', 'ME', 'ME1', 'ME2', 'ME3', 'ME4',
])
export const PayFrequencySchema = z.enum(['daily', 'weekly', 'semi_monthly', 'monthly'])
export const SexSchema = z.enum(['male', 'female'])

export const CreateEmployeeSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  employeeCode: z.string().min(1).max(20),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  fullName: z.string().min(1).max(200),
  middleName: z.string().max(100).optional(),
  suffix: z.string().max(10).optional(),
  sex: SexSchema,
  civilStatus: CivilStatusSchema.default('single'),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  birthplace: z.string().max(200).optional(),
  nationality: z.string().max(100).default('Filipino'),
  phone: phoneSchema,
  email: emailSchema.optional(),
  address: z.string().min(1).max(500),
  barangay: z.string().max(100).optional(),
  city: z.string().min(1).max(100),
  province: z.string().min(1).max(100),
  photoUrl: z.string().url().optional(),
  department: z.string().min(1).max(100),
  position: z.string().min(1).max(200),
  employmentStatus: EmploymentStatusSchema.default('probationary'),
  dateHired: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  dateRegularized: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateResigned: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  payFrequency: PayFrequencySchema.default('semi_monthly'),
  basicSalary: z.number().positive('Basic salary must be positive'),
  tin: z.string().regex(/^\d{9,12}$/, 'TIN must be 9-12 digits'),
  taxStatus: TaxStatusSchema.default('S'),
  hasOtherEmployer: z.boolean().default(false),
  otherEmployerName: z.string().max(200).optional(),
  sssNumber: z.string().regex(/^\d{2}-\d{7}-\d{1}$/, 'SSS format: XX-XXXXXXX-X'),
  philhealthNumber: z.string().regex(/^\d{2}-\d{9}-\d{1}$/, 'PhilHealth format: XX-XXXXXXXXX-X'),
  pagibigNumber: z.string().regex(/^\d{4}-\d{4}-\d{4}$/, 'Pag-IBIG format: XXXX-XXXX-XXXX'),
  pagibigContribution: z.number().min(100).max(200).default(100),
  bankName: z.string().max(100).optional(),
  bankAccountNumber: z.string().max(30).optional(),
  bankAccountType: z.enum(['savings', 'checking']).optional(),
  emergencyContactName: z.string().min(1).max(200),
  emergencyContactPhone: phoneSchema,
  emergencyContactRelation: z.string().max(100).optional(),
  customFields: z.record(z.unknown()).optional(),
})

export const UpdateEmployeeSchema = createUpdateSchema({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  fullName: z.string().min(1).max(200).optional(),
  middleName: z.string().max(100).optional(),
  suffix: z.string().max(10).optional(),
  sex: SexSchema.optional(),
  civilStatus: CivilStatusSchema.optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  address: z.string().min(1).max(500).optional(),
  department: z.string().min(1).max(100).optional(),
  position: z.string().min(1).max(200).optional(),
  employmentStatus: EmploymentStatusSchema.optional(),
  payFrequency: PayFrequencySchema.optional(),
  basicSalary: z.number().positive().optional(),
  taxStatus: TaxStatusSchema.optional(),
  hasOtherEmployer: z.boolean().optional(),
  pagibigContribution: z.number().min(100).max(200).optional(),
  bankName: z.string().max(100).optional(),
  bankAccountNumber: z.string().max(30).optional(),
  bankAccountType: z.enum(['savings', 'checking']).optional(),
  emergencyContactName: z.string().min(1).max(200).optional(),
  emergencyContactPhone: phoneSchema.optional(),
  customFields: z.record(z.unknown()).optional(),
})

export const EmployeeQuerySchema = createQuerySchema({
  department: z.string().optional(),
  employmentStatus: EmploymentStatusSchema.optional(),
  payFrequency: PayFrequencySchema.optional(),
})

export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  probationary: 'Probationary',
  regular: 'Regular',
  contractual: 'Contractual',
  project_based: 'Project-Based',
  seasonal: 'Seasonal',
  resigned: 'Resigned',
  terminated: 'Terminated',
  retired: 'Retired',
}

export const TAX_STATUS_LABELS: Record<TaxStatus, string> = {
  S: 'Single / No Dependents',
  S1: 'Single / 1 Dependent',
  S2: 'Single / 2 Dependents',
  S3: 'Single / 3 Dependents',
  S4: 'Single / 4 Dependents',
  ME: 'Married / No Dependents',
  ME1: 'Married / 1 Dependent',
  ME2: 'Married / 2 Dependents',
  ME3: 'Married / 3 Dependents',
  ME4: 'Married / 4 Dependents',
}

export const TAX_EXEMPTION_THRESHOLD: Record<TaxStatus, number> = {
  S: 250000, S1: 250000, S2: 250000, S3: 250000, S4: 250000,
  ME: 250000, ME1: 250000, ME2: 250000, ME3: 250000, ME4: 250000,
}

// ═══════════════════════════════════════════════════════════════
// Attendance
// ═══════════════════════════════════════════════════════════════

export type AttendanceSource = 'biometric' | 'mobile_gps' | 'mobile_photo' | 'manual' | 'wifi'
export type AttendanceType = 'time_in' | 'time_out' | 'break_out' | 'break_in' | 'overtime_in' | 'overtime_out'
export type ShiftType = 'regular' | 'night_shift' | 'flexible'

export interface AttendanceRecord {
  id: string
  tenantId: string
  branchId?: string
  employeeId: string
  employeeName: string          // denormalized
  attendanceDate: string         // YYYY-MM-DD
  logTime: number                // Unix timestamp ms
  type: AttendanceType
  source: AttendanceSource

  // ─── Biometric / Device Info ────────────────────────────
  deviceId?: string              // Biometric machine ID
  deviceName?: string
  terminalId?: string

  // ─── Mobile GPS ─────────────────────────────────────────
  latitude?: number
  longitude?: number
  locationName?: string
  photoUrl?: string              // Selfie for photo-based attendance
  gpsAccuracy?: number

  // ─── Computed ───────────────────────────────────────────
  isLate: boolean
  lateMinutes: number
  isOvertime: boolean
  overtimeMinutes: number
  isUndertime: boolean
  undertimeMinutes: number

  // ─── Shift ──────────────────────────────────────────────
  shiftId?: string
  shiftName?: string
  scheduledStart?: string         // HH:MM
  scheduledEnd?: string           // HH:MM

  // ─── Approval ───────────────────────────────────────────
  status: 'pending' | 'approved' | 'rejected'
  approvedBy?: string
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const AttendanceSourceSchema = z.enum(['biometric', 'mobile_gps', 'mobile_photo', 'manual', 'wifi'])
export const AttendanceTypeSchema = z.enum(['time_in', 'time_out', 'break_out', 'break_in', 'overtime_in', 'overtime_out'])

export const CreateAttendanceSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  employeeId: z.string().min(1),
  employeeName: z.string().min(1).max(200),
  attendanceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  logTime: z.number().positive(),
  type: AttendanceTypeSchema,
  source: AttendanceSourceSchema,
  deviceId: z.string().optional(),
  deviceName: z.string().optional(),
  terminalId: z.string().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  locationName: z.string().max(300).optional(),
  photoUrl: z.string().optional(),
  gpsAccuracy: z.number().min(0).optional(),
  shiftId: z.string().optional(),
  notes: notesSchema,
})

// ═══════════════════════════════════════════════════════════════
// Shifts
// ═══════════════════════════════════════════════════════════════

export interface Shift {
  id: string
  tenantId: string
  branchId?: string
  shiftCode: string
  name: string
  type: ShiftType
  startTime: string               // HH:MM
  endTime: string                 // HH:MM
  breakStart?: string
  breakEnd?: string
  gracePeriodMinutes: number      // Late grace period
  isActive: boolean
  color?: string                  // Display color in calendar

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreateShiftSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  shiftCode: z.string().min(1).max(20),
  name: z.string().min(1).max(100),
  type: z.enum(['regular', 'night_shift', 'flexible']).default('regular'),
  startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
  endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
  breakStart: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  breakEnd: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  gracePeriodMinutes: z.number().int().min(0).default(15),
  isActive: z.boolean().default(true),
  color: z.string().max(20).optional(),
})

// ═══════════════════════════════════════════════════════════════
// Biometric Device
// ═══════════════════════════════════════════════════════════════

export type BiometricDeviceType = 'zkteco' | 'suprema' | 'hikvision' | 'fingerprint' | 'face_recognition' | 'generic'
export type BiometricProtocol = 'tcp' | 'usb' | 'wifi' | 'cloud_api'

export interface BiometricDevice {
  id: string
  tenantId: string
  branchId?: string
  deviceCode: string
  name: string
  deviceType: BiometricDeviceType
  protocol: BiometricProtocol
  ipAddress?: string
  port?: number
  terminalId?: string             // Device terminal ID (e.g., ZKTeco terminal number)
  serialNumber?: string
  location: string                // Physical location description
  isActive: boolean
  lastSyncAt?: number
  syncIntervalMinutes: number     // How often to pull logs

  // ─── Metadata-driven device config ──────────────────────
  deviceConfig?: Record<string, unknown>

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const BiometricDeviceTypeSchema = z.enum(['zkteco', 'suprema', 'hikvision', 'fingerprint', 'face_recognition', 'generic'])
export const BiometricProtocolSchema = z.enum(['tcp', 'usb', 'wifi', 'cloud_api'])

export const CreateBiometricDeviceSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  deviceCode: z.string().min(1).max(20),
  name: z.string().min(1).max(200),
  deviceType: BiometricDeviceTypeSchema,
  protocol: BiometricProtocolSchema,
  ipAddress: z.string().ip().optional(),
  port: z.number().int().min(1).max(65535).optional(),
  serialNumber: z.string().max(100).optional(),
  location: z.string().min(1).max(300),
  isActive: z.boolean().default(true),
  syncIntervalMinutes: z.number().int().min(1).max(1440).default(15),
  deviceConfig: z.record(z.unknown()).optional(),
})

// ═══════════════════════════════════════════════════════════════
// Payroll Period & Payslip
// ═══════════════════════════════════════════════════════════════

export type PayrollStatus = 'draft' | 'computed' | 'approved' | 'disbursed' | 'cancelled'

export interface PayrollPeriod {
  id: string
  tenantId: string
  branchId?: string
  periodCode: string
  name: string
  payFrequency: PayFrequency
  periodStart: string             // YYYY-MM-DD
  periodEnd: string               // YYYY-MM-DD
  paymentDate: string             // YYYY-MM-DD (date of disbursement)
  status: PayrollStatus
  fiscalYear: number
  fiscalMonth: number
  totalEmployees: number
  totalGrossPay: number
  totalDeductions: number
  totalNetPay: number
  preparedBy: string
  approvedBy?: string
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export interface Payslip {
  id: string
  tenantId: string
  payrollPeriodId: string
  employeeId: string
  employeeName: string
  employeeCode: string
  department: string
  position: string

  // ─── Earnings ────────────────────────────────────────────
  basicPay: number               // Basic salary for this period
  overtimePay: number
  holidayPay: number
  nightDifferential: number
  allowance: number
  otherEarnings: number
  thirteenthMonthPay: number     // Pro-rated per period if applicable
  grossPay: number               // Sum of all earnings

  // ─── Government Deductions ──────────────────────────────
  sssContribution: number
  philhealthContribution: number
  pagibigContribution: number
  withholdingTax: number         // BIR graduated withholding tax

  // ─── Other Deductions ───────────────────────────────────
  sssLoan: number
  pagibigLoan: number
  cashAdvances: number
  lateDeduction: number
  absenceDeduction: number
  otherDeductions: number
  totalDeductions: number

  // ─── Summary ────────────────────────────────────────────
  netPay: number

  // ─── Attendance Summary ─────────────────────────────────
  daysWorked: number
  hoursWorked: number
  overtimeHours: number
  lateMinutes: number
  absences: number

  // ─── Metadata ───────────────────────────────────────────
  periodStart: string
  periodEnd: string
  payFrequency: PayFrequency

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

// ═══════════════════════════════════════════════════════════════
// Form 2316 (BIR Certificate of Compensation Payment)
// ═══════════════════════════════════════════════════════════════

export interface Form2316 {
  id: string
  tenantId: string
  employeeId: string
  employeeName: string
  employeeTIN: string
  taxYear: number                // e.g. 2025

  // ─── Part I: Employee Information ───────────────────────
  employeeAddress: string
  employeeBirthDate: string
  employeeCivilStatus: CivilStatus
  employeeTaxStatus: TaxStatus

  // ─── Part II: Employer Information ───────────────────────
  employerName: string           // From tenant metadata
  employerTIN: string
  employerAddress: string
  employerZipCode?: string

  // ─── Part III: Compensation Income ──────────────────────
  grossCompensation: number      // Total basic salary for the year
  thirteenthMonthPay: number
  otherBonuses: number
  deMinimisBenefits: number
  totalCompensation: number

  // ─── Part IV: Non-Taxable / Exempt ──────────────────────
  nonTaxableThirteenthMonth: number  // Up to 90,000 exempt
  nonTaxableDeMinimis: number
  sssGsisContributions: number
  philhealthContributions: number
  pagibigContributions: number
  totalExempt: number

  // ─── Part V: Taxable Income ─────────────────────────────
  taxableIncome: number          // totalCompensation - totalExempt

  // ─── Part VI: Tax Due ───────────────────────────────────
  taxDue: number                 // Computed using graduated rates
  taxWithheldPreviousEmployer?: number
  totalTaxWithheld: number
  taxOverWithheld: number
  taxUnderWithheld: number

  // ─── Part VII: Signatures & Dates ───────────────────────
  preparedBy: string
  preparedDate: string
  certifiedBy?: string
  certifiedDate?: string
  status: 'draft' | 'final' | 'filed'

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreateForm2316Schema = z.object({
  tenantId: z.string().min(1),
  employeeId: z.string().min(1),
  taxYear: z.number().int().min(2020).max(2030),
  employerName: z.string().min(1).max(500),
  employerTIN: z.string().min(1).max(20),
  employerAddress: z.string().min(1).max(500),
  employerZipCode: z.string().max(10).optional(),
  thirteenthMonthPay: z.number().min(0).default(0),
  otherBonuses: z.number().min(0).default(0),
  deMinimisBenefits: z.number().min(0).default(0),
  taxWithheldPreviousEmployer: z.number().min(0).optional(),
  preparedBy: z.string().min(1).max(200),
})

// ═══════════════════════════════════════════════════════════════
// Metadata-driven Payroll Config (per tenant)
// ═══════════════════════════════════════════════════════════════

export interface PayrollConfig {
  /** Company name (for Form 2316, payslip header) */
  companyName: string
  companyTIN: string
  companyAddress: string
  companyZipCode?: string

  /** Default pay frequency */
  defaultPayFrequency: PayFrequency

  /** Semi-monthly period definitions */
  semiMonthFirstStart: number     // Day of month (default 1)
  semiMonthFirstEnd: number       // Day of month (default 15)
  semiMonthSecondStart: number    // Day of month (default 16)
  semiMonthSecondEnd: number      // Day of month (default last day)

  /** Contribution rates (metadata-driven — can change yearly) */
  sssEmployeeRate: number         // e.g. 0.045 (4.5%)
  sssEmployerRate: number         // e.g. 0.095 (9.5%)
  philhealthRate: number          // e.g. 0.05 (5%)
  philhealthSplitEqual: boolean   // If true, split equally

  /** Pag-IBIG */
  pagibigMinEmployeeContribution: number  // 100
  pagibigMaxEmployeeContribution: number  // 200
  pagibigSalaryThreshold: number          // Above this, max contribution

  /** Overtime rates */
  overtimeRateNormal: number      // e.g. 1.25 (125% on regular days)
  overtimeRateRestDay: number     // e.g. 1.30 (130%)
  overtimeRateSpecialHoliday: number // e.g. 1.30
  overtimeRateRegularHoliday: number // e.g. 2.00

  /** Night differential (10pm-6am) */
  nightDiffRate: number           // e.g. 0.10 (10%)

  /** 13th Month Pay */
  thirteenthMonthEnabled: boolean
  thirteenthMonthExemptCap: number  // 90,000 PHP exempt from tax

  /** De Minimis benefits caps (tax-exempt) */
  deMinimisRiceSubsidy: number
  deMinimisUniformAllowance: number
  deMinimisMedicalAllowance: number
  deMinimisLaundryAllowance: number

  /** Biometric integration */
  biometricEnabled: boolean
  biometricSyncMethod: 'tcp' | 'api_pull' | 'csv_import' | 'mobile'
  biometricAutoSyncMinutes: number
}

export const DEFAULT_PAYROLL_CONFIG: PayrollConfig = {
  companyName: 'Sample Cooperative',
  companyTIN: '000-000-000-000',
  companyAddress: '123 Main Street, Manila, Philippines',
  companyZipCode: '1000',

  defaultPayFrequency: 'semi_monthly',

  semiMonthFirstStart: 1,
  semiMonthFirstEnd: 15,
  semiMonthSecondStart: 16,
  semiMonthSecondEnd: 0, // 0 = last day of month

  // 2025 Contribution Rates
  sssEmployeeRate: 0.045,
  sssEmployerRate: 0.095,
  philhealthRate: 0.05,
  philhealthSplitEqual: true,

  pagibigMinEmployeeContribution: 100,
  pagibigMaxEmployeeContribution: 200,
  pagibigSalaryThreshold: 1500,

  overtimeRateNormal: 1.25,
  overtimeRateRestDay: 1.30,
  overtimeRateSpecialHoliday: 1.30,
  overtimeRateRegularHoliday: 2.00,

  nightDiffRate: 0.10,

  thirteenthMonthEnabled: true,
  thirteenthMonthExemptCap: 90000,

  deMinimisRiceSubsidy: 2000,
  deMinimisUniformAllowance: 6000,
  deMinimisMedicalAllowance: 1500,
  deMinimisLaundryAllowance: 300,

  biometricEnabled: false,
  biometricSyncMethod: 'tcp',
  biometricAutoSyncMinutes: 15,
}
